import { faSearch } from "@jewlr/pro-light-svg-icons/faSearch"
import { faUser } from "@jewlr/pro-light-svg-icons/faUser"
import loadable from "@loadable/component"
import { useQueryClient } from "@tanstack/react-query"
import Cookies from "js-cookie"
import PropTypes from "prop-types"
import { useCallback, useEffect, useRef, useState } from "react"
import { connect } from "react-redux"
import { Link, useLocation } from "react-router-dom"

import api from "apis"
import {
  clearSearchUserInput,
  clearSearchSuggestions,
} from "app-store-v2/app/actions"
import CartIcon from "areas/header/dropdowns/cart-icon"
import withHeader from "areas/header/hoc/with-header"
import WishlistIcon from "areas/header/jewlr/wishlist-icon"
import NavIcon from "areas/header/nav-icon"
import Button from "components/ui/button"
import Image from "components/ui/image"
import Media from "components/ui/media"
import { isTestMode, retry } from "helpers/application"

import BurgerMenu from "./burger-menu"

const HeaderSearch = loadable(
  () => retry(() => import("areas/header/jewlr/search/search")),
  { ssr: false }
)

const MobileHeader = ({
  clearSearch,
  toggleMenu,
  showAccountMenu,
  toggleAccountMenu,
  loggedIn,
}) => {
  const [searching, setSearch] = useState(false)
  const [geo, setGeo] = useState(null)
  const searchInputRef = useRef()
  const searchBtnRef = useRef()
  const restoreSearchFocus = useRef()
  const location = useLocation()
  const queryClient = useQueryClient()

  const trackNavClick = useCallback((navElement) => {
    if (!isTestMode) {
      dataLayer.push({
        element: navElement,
        event: "clicked-nav-element",
      })
    }
  }, [])

  useEffect(() => {
    toggleMenu(true)
    toggleAccountMenu(true)
    setSearch(false)
    clearSearch()
  }, [location.pathname])

  useEffect(() => {
    setGeo(Cookies.get("geo"))
  }, [geo])

  useEffect(() => {
    if (!searching && restoreSearchFocus.current && searchBtnRef.current) {
      searchBtnRef.current.focus()
      restoreSearchFocus.current = false
    }
  }, [searching, searchBtnRef.current])

  const toggleSearchBar = (e) => {
    e.preventDefault()
    setSearch(!searching)
    toggleMenu(true)
    toggleAccountMenu(true)
  }

  const handleCloseSearch = () => {
    restoreSearchFocus.current = true
    setSearch(false)
  }

  const prefetchSearch = useCallback(() => {
    HeaderSearch.preload()
    queryClient.prefetchQuery({
      queryFn: () => api.get("/api/v10/utilities/search_suggestions"),
      queryKey: ["search_suggestions"],
    })
  }, [queryClient])

  return (
    <Media
      className="h-(--mobile-header-height) relative flex w-full"
      lessThan="desktop"
    >
      {searching ? (
        <div className="flex items-center">
          <HeaderSearch
            handleCloseSearch={handleCloseSearch}
            isMobile
            searchInputRef={searchInputRef}
          />
        </div>
      ) : (
        <div className="h-(--mobile-header-height) flex w-full items-center justify-between px-16">
          <Link
            className="inline-block"
            data-lc="home"
            onClick={() => trackNavClick("logo")}
            to="/"
          >
            <Image
              alt="Jewlr"
              className="w-(--base-width) mobile:w-(--mobile-width)"
              height="139"
              lazyload={false}
              src={require("images/layout/logo/jewlr-logo.svg")}
              style={{
                "--base-width": "101px",
                "--mobile-width": "139px",
              }}
              width="701"
            />
          </Link>
          <div className="flex items-center justify-between">
            <div className="tablet:gap-24 flex items-center gap-16">
              <Button
                aria-label={loggedIn ? "My Account" : "Sign In"}
                data-cy="menu-sign-in-mobile"
                onClick={() => {
                  trackNavClick("account")
                  toggleAccountMenu(showAccountMenu)
                }}
                {...(loggedIn && {
                  "aria-controls": "mobile-menu-account",
                  "aria-expanded": showAccountMenu,
                  "aria-haspopup": "true",
                })}
              >
                <NavIcon icon={faUser} />
              </Button>

              <WishlistIcon onNavClick={() => trackNavClick("wishlist")} />

              <Button
                aria-label="Search"
                data-cy="header-mobile-search-icon"
                onClick={(e) => {
                  trackNavClick("search")
                  toggleSearchBar(e)
                }}
                onFocus={prefetchSearch}
                onMouseEnter={prefetchSearch}
                onTouchStart={prefetchSearch}
                ref={searchBtnRef}
              >
                <NavIcon icon={faSearch} />
              </Button>
              <CartIcon isMobile onNavClick={() => trackNavClick("cart")} />
            </div>
            <BurgerMenu onNavClick={() => trackNavClick("menu")} />
          </div>
        </div>
      )}
    </Media>
  )
}

MobileHeader.propTypes = {
  clearSearch: PropTypes.func,
  loggedIn: PropTypes.bool,
  showAccountMenu: PropTypes.bool,
  toggleAccountMenu: PropTypes.func,
  toggleMenu: PropTypes.func,
}

const mapStateToProps = () => {
  return {}
}

const mapDispatchToProps = (dispatch) => {
  return {
    clearSearch() {
      dispatch(clearSearchUserInput())
      dispatch(clearSearchSuggestions())
    },
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withHeader(MobileHeader))
